import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  BarChart3, DollarSign, Eye, MousePointerClick, Users, TrendingUp,
  Instagram, Facebook, MessageCircle, Heart, Send, ExternalLink, Image,
} from 'lucide-react';

const fmtNum = (n) => (n || 0).toLocaleString();
const fmtCur = (n, currency = 'AED') => `${currency} ${(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ title, value, icon: Icon, subtitle }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-xl font-bold mt-0.5">{value}</p>
            {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {Icon && <Icon className="h-8 w-8 text-muted-foreground/30" />}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MetaHubPage() {
  const [activeTab, setActiveTab] = useState('ads');
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('AED');
  const [adSummary, setAdSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [pagePosts, setPagePosts] = useState([]);
  const [igMedia, setIgMedia] = useState([]);
  const [selectedIgId, setSelectedIgId] = useState('');
  const [comments, setComments] = useState([]);
  const [selectedMediaId, setSelectedMediaId] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  // DM state
  const [dmConversations, setDmConversations] = useState([]);
  const [dmError, setDmError] = useState('');
  const [dmPlatform, setDmPlatform] = useState('instagram'); // instagram | messenger
  const [selectedDmPage, setSelectedDmPage] = useState('');
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [dmMessages, setDmMessages] = useState([]);
  const [dmReplyText, setDmReplyText] = useState('');
  const [dmLoading, setDmLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  // Load ad accounts and pages on mount
  useEffect(() => {
    apiClient.get('/meta/ad-accounts').then(r => {
      const accts = Array.isArray(r.data) ? r.data : [];
      setAdAccounts(accts);
      if (accts.length > 0) {
        setSelectedAccount(accts[0].id);
        setSelectedCurrency(accts[0].currency || 'AED');
      }
    }).catch(() => {});
    apiClient.get('/meta/pages').then(r => {
      const p = Array.isArray(r.data) ? r.data : [];
      setPages(p);
      if (p.length > 0) {
        setSelectedPage(p[0].id);
        setSelectedIgId(p[0].ig_id);
      }
    }).catch(() => {});
  }, []);

  // Load ad data when account or dates change
  const loadAds = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const [summary, insights] = await Promise.all([
        apiClient.get(`/meta/ad-summary/${selectedAccount}?date_from=${dateFrom}&date_to=${dateTo}`),
        apiClient.get(`/meta/ad-insights/${selectedAccount}?date_from=${dateFrom}&date_to=${dateTo}`),
      ]);
      setAdSummary(summary.data);
      setCampaigns(Array.isArray(insights.data) ? insights.data : []);
    } catch (e) {
      toast.error('Failed to load ad data');
    }
    setLoading(false);
  }, [selectedAccount, dateFrom, dateTo]);

  useEffect(() => { if (activeTab === 'ads') loadAds(); }, [loadAds, activeTab]);

  // Load page posts
  const loadPagePosts = useCallback(async () => {
    if (!selectedPage) return;
    try {
      const r = await apiClient.get(`/meta/page/${selectedPage}/posts?limit=20`);
      setPagePosts(Array.isArray(r.data) ? r.data : []);
    } catch { setPagePosts([]); }
  }, [selectedPage]);

  useEffect(() => { if (activeTab === 'pages') loadPagePosts(); }, [loadPagePosts, activeTab]);

  // Load Instagram media
  const loadIgMedia = useCallback(async () => {
    if (!selectedIgId) return;
    try {
      const r = await apiClient.get(`/meta/instagram/${selectedIgId}/media?limit=20`);
      setIgMedia(Array.isArray(r.data) ? r.data : []);
    } catch { setIgMedia([]); }
  }, [selectedIgId]);

  useEffect(() => { if (activeTab === 'instagram') loadIgMedia(); }, [loadIgMedia, activeTab]);

  // Load comments for selected media
  const loadComments = async (mediaId) => {
    setSelectedMediaId(mediaId);
    try {
      const r = await apiClient.get(`/meta/instagram/media/${mediaId}/comments`);
      setComments(Array.isArray(r.data) ? r.data : []);
    } catch { setComments([]); }
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    try {
      await apiClient.post(`/meta/instagram/comments/${commentId}/reply`, { message: replyText });
      toast.success('Reply sent');
      setReplyText('');
      setReplyingTo(null);
      loadComments(selectedMediaId);
    } catch { toast.error('Failed to send reply'); }
  };

  // DM functions
  const loadDmConversations = useCallback(async () => {
    if (!selectedDmPage) return;
    setDmLoading(true);
    setDmError('');
    try {
      const r = await apiClient.get(`/meta/instagram/dm/${selectedDmPage}/conversations?limit=25&platform=${dmPlatform}`);
      // New shape: {conversations: [...], error?: str}. Tolerant of legacy array too.
      if (Array.isArray(r.data)) {
        setDmConversations(r.data);
      } else {
        setDmConversations(Array.isArray(r.data?.conversations) ? r.data.conversations : []);
        if (r.data?.error) setDmError(r.data.error);
      }
    } catch {
      setDmConversations([]);
      setDmError('Failed to load conversations');
    }
    setDmLoading(false);
  }, [selectedDmPage, dmPlatform]);

  useEffect(() => { if (activeTab === 'dms' && selectedDmPage) loadDmConversations(); }, [loadDmConversations, activeTab, selectedDmPage]);

  useEffect(() => {
    if (pages.length > 0 && !selectedDmPage) setSelectedDmPage(pages[0].id);
  }, [pages, selectedDmPage]);

  const openConversation = async (convo) => {
    setSelectedConvo(convo);
    setDmMessages([]);
    try {
      const r = await apiClient.get(`/meta/instagram/dm/${selectedDmPage}/messages/${convo.id}?limit=30`);
      setDmMessages(Array.isArray(r.data) ? r.data : []);
    } catch { setDmMessages([]); }
  };

  const sendDmReply = async () => {
    if (!dmReplyText.trim() || !selectedConvo) return;
    const otherParticipant = selectedConvo.participants?.find(p => p.id !== selectedDmPage);
    if (!otherParticipant) { toast.error('Cannot determine recipient'); return; }
    try {
      await apiClient.post(`/meta/instagram/dm/${selectedDmPage}/send`, {
        recipient_id: otherParticipant.id,
        message: dmReplyText,
      });
      toast.success('DM sent');
      setDmReplyText('');
      openConversation(selectedConvo);
    } catch { toast.error('Failed to send DM'); }
  };

  return (
    <div className="space-y-6" data-testid="meta-hub-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Facebook className="h-6 w-6 text-blue-600" /> Meta Hub
        </h1>
        <p className="text-muted-foreground text-sm">Facebook Ads, Instagram, and Page management</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="ads" data-testid="meta-tab-ads"><BarChart3 className="h-4 w-4 mr-1.5" /> Ad Performance</TabsTrigger>
          <TabsTrigger value="instagram" data-testid="meta-tab-ig"><Instagram className="h-4 w-4 mr-1.5" /> Instagram</TabsTrigger>
          <TabsTrigger value="dms" data-testid="meta-tab-dms"><MessageCircle className="h-4 w-4 mr-1.5" /> DMs</TabsTrigger>
          <TabsTrigger value="pages" data-testid="meta-tab-pages"><Facebook className="h-4 w-4 mr-1.5" /> Pages</TabsTrigger>
        </TabsList>

        {/* ═══ AD PERFORMANCE ═══ */}
        <TabsContent value="ads" className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Ad Account</Label>
              <Select value={selectedAccount} onValueChange={v => {
                setSelectedAccount(v);
                const acct = adAccounts.find(a => a.id === v);
                setSelectedCurrency(acct?.currency || 'AED');
              }}>
                <SelectTrigger className="w-[250px]" data-testid="meta-ad-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {adAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.status})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[140px] h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[140px] h-9" />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" /></div>
          ) : adSummary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard title="Total Spend" value={fmtCur(adSummary.spend, selectedCurrency)} icon={DollarSign} />
                <StatCard title="Impressions" value={fmtNum(adSummary.impressions)} icon={Eye} />
                <StatCard title="Clicks" value={fmtNum(adSummary.clicks)} icon={MousePointerClick} />
                <StatCard title="Reach" value={fmtNum(adSummary.reach)} icon={Users} />
                <StatCard title="Leads" value={fmtNum(adSummary.leads)} icon={TrendingUp} />
              </div>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Campaign Breakdown</CardTitle></CardHeader>
                <CardContent>
                  <ScrollArea className="max-h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Campaign</TableHead>
                          <TableHead className="text-right">Spend</TableHead>
                          <TableHead className="text-right">Impressions</TableHead>
                          <TableHead className="text-right">Clicks</TableHead>
                          <TableHead className="text-right">CTR</TableHead>
                          <TableHead className="text-right">CPC</TableHead>
                          <TableHead className="text-right">Leads</TableHead>
                          <TableHead className="text-right">CPL</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaigns.map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium text-sm max-w-[200px] truncate">{c.campaign_name}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{fmtCur(c.spend, selectedCurrency)}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{fmtNum(c.impressions)}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{fmtNum(c.clicks)}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{(c.ctr || 0).toFixed(2)}%</TableCell>
                            <TableCell className="text-right font-mono text-sm">{fmtCur(c.cpc)}</TableCell>
                            <TableCell className="text-right font-mono text-sm font-bold text-emerald-600">{c.leads || 0}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{c.cost_per_lead ? fmtCur(c.cost_per_lead) : '—'}</TableCell>
                          </TableRow>
                        ))}
                        {campaigns.length === 0 && (
                          <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No campaign data for this period</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ═══ INSTAGRAM ═══ */}
        <TabsContent value="instagram" className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Instagram Account</Label>
              <Select value={selectedIgId} onValueChange={(v) => {
                setSelectedIgId(v);
                setSelectedMediaId('');
                setComments([]);
              }}>
                <SelectTrigger className="w-[250px]" data-testid="meta-ig-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pages.filter(p => p.ig_id).map(p => (
                    <SelectItem key={p.ig_id} value={p.ig_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Media Grid */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Image className="h-4 w-4" /> Recent Posts</CardTitle></CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-2">
                    {igMedia.map(m => (
                      <button key={m.id}
                        onClick={() => loadComments(m.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${selectedMediaId === m.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                        data-testid={`ig-media-${m.id}`}>
                        <p className="text-sm line-clamp-2">{m.caption || 'No caption'}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {m.like_count || 0}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {m.comments_count || 0}</span>
                          <span>{new Date(m.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                          <Badge variant="outline" className="text-[9px]">{m.media_type}</Badge>
                        </div>
                      </button>
                    ))}
                    {igMedia.length === 0 && <p className="text-center py-8 text-muted-foreground">No media found</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Comments Panel */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Comments</CardTitle></CardHeader>
              <CardContent>
                {selectedMediaId ? (
                  <ScrollArea className="max-h-[500px]">
                    <div className="space-y-3">
                      {comments.map(c => (
                        <div key={c.id} className="p-3 border rounded-lg space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="font-semibold text-sm">@{c.username}</span>
                              <p className="text-sm mt-0.5">{c.text}</p>
                              <span className="text-[10px] text-muted-foreground">{new Date(c.timestamp).toLocaleString()}</span>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)} data-testid={`ig-reply-btn-${c.id}`}>
                              <Send className="h-3 w-3" />
                            </Button>
                          </div>
                          {/* Replies */}
                          {c.replies?.data?.map(r => (
                            <div key={r.id} className="ml-6 p-2 bg-muted/50 rounded text-xs">
                              <span className="font-medium">@{r.username}</span>: {r.text}
                            </div>
                          ))}
                          {/* Reply Input */}
                          {replyingTo === c.id && (
                            <div className="flex gap-2 ml-6">
                              <Textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                                placeholder="Write a reply..." rows={1} className="text-sm flex-1" />
                              <Button size="sm" onClick={() => handleReply(c.id)} disabled={!replyText.trim()}>
                                <Send className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      {comments.length === 0 && <p className="text-center py-8 text-muted-foreground">No comments on this post</p>}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">Select a post to view comments</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ═══ INSTAGRAM DMs ═══ */}
        <TabsContent value="dms" className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Page</Label>
              <Select value={selectedDmPage} onValueChange={setSelectedDmPage}>
                <SelectTrigger className="w-[230px]" data-testid="meta-dm-page-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pages.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Platform</Label>
              <Select value={dmPlatform} onValueChange={setDmPlatform}>
                <SelectTrigger className="w-[170px]" data-testid="meta-dm-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="messenger">Facebook Messenger</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={loadDmConversations} data-testid="dm-refresh">
              Refresh
            </Button>
          </div>

          {dmError && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-800 dark:text-amber-300" data-testid="dm-error">
              <strong>Heads up:</strong> {dmError}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: 500 }}>
            {/* Conversation List */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" /> Conversations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {dmLoading ? (
                  <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" /></div>
                ) : (
                  <ScrollArea className="max-h-[450px]">
                    <div className="divide-y">
                      {dmConversations.map(c => {
                        const other = c.participants?.find(p => p.id !== selectedDmPage);
                        return (
                          <button
                            key={c.id}
                            onClick={() => openConversation(c)}
                            className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${selectedConvo?.id === c.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                            data-testid={`dm-convo-${c.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {(other?.name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-sm truncate">{other?.name || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground truncate">{c.last_message?.text || 'No messages'}</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {c.updated_time ? new Date(c.updated_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      {dmConversations.length === 0 && !dmLoading && (
                        <p className="text-center py-10 text-muted-foreground text-sm">No DM conversations found</p>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Message Thread */}
            <Card className="lg:col-span-2 flex flex-col">
              <CardHeader className="pb-2 border-b">
                <CardTitle className="text-base flex items-center gap-2">
                  {selectedConvo ? (
                    <>
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white flex items-center justify-center text-xs font-bold">
                        {(selectedConvo.participants?.find(p => p.id !== selectedDmPage)?.name || '?').charAt(0).toUpperCase()}
                      </div>
                      {selectedConvo.participants?.find(p => p.id !== selectedDmPage)?.name || 'Conversation'}
                    </>
                  ) : 'Select a conversation'}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col p-0">
                {selectedConvo ? (
                  <>
                    <ScrollArea className="flex-1 p-4" style={{ maxHeight: 380 }}>
                      <div className="space-y-3">
                        {[...dmMessages].reverse().map(m => {
                          const isMe = m.from_id === selectedDmPage;
                          return (
                            <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[70%] p-3 rounded-2xl text-sm ${
                                isMe
                                  ? 'bg-primary text-primary-foreground rounded-br-sm'
                                  : 'bg-muted rounded-bl-sm'
                              }`}>
                                <p>{m.text}</p>
                                {m.attachments?.length > 0 && (
                                  <div className="mt-1 space-y-1">
                                    {m.attachments.map((a, i) => (
                                      a.url ? <img key={i} src={a.url} alt="" className="rounded max-w-full max-h-32" /> :
                                      <Badge key={i} variant="outline" className="text-[10px]">{a.type || 'attachment'}</Badge>
                                    ))}
                                  </div>
                                )}
                                <p className={`text-[10px] mt-1 ${isMe ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                                  {m.from_name} &middot; {m.time ? new Date(m.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        {dmMessages.length === 0 && <p className="text-center py-8 text-muted-foreground">No messages loaded</p>}
                      </div>
                    </ScrollArea>
                    <div className="p-3 border-t flex gap-2">
                      <Textarea value={dmReplyText} onChange={e => setDmReplyText(e.target.value)}
                        placeholder="Type a reply..." rows={1} className="flex-1 resize-none"
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendDmReply(); } }}
                        data-testid="dm-reply-input" />
                      <Button onClick={sendDmReply} disabled={!dmReplyText.trim()} data-testid="dm-send-btn">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Select a conversation to view messages</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ═══ PAGES ═══ */}
        <TabsContent value="pages" className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Page</Label>
              <Select value={selectedPage} onValueChange={setSelectedPage}>
                <SelectTrigger className="w-[250px]" data-testid="meta-page-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pages.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Recent Posts</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[500px]">
                <div className="space-y-3">
                  {pagePosts.map(p => (
                    <div key={p.id} className="p-3 border rounded-lg">
                      <p className="text-sm">{p.message || 'No text'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {p.likes?.summary?.total_count || 0}</span>
                        <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {p.comments?.summary?.total_count || 0}</span>
                        <span>{p.shares?.count || 0} shares</span>
                        <span>{new Date(p.created_time).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                  {pagePosts.length === 0 && <p className="text-center py-8 text-muted-foreground">No posts found</p>}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
